export function checkSize(size) {
  return window.matchMedia(`only screen and (max-width: ${size}px)`).matches;
}

const query = document.querySelector.bind(document);
const queryAll = document.querySelectorAll.bind(document);

export function hideElement(selectorOrElements) {
  let errorMessage = "Unrecognized type for first argument 'selectorOrElements'. The value must be a selector string, Node, or NodeList.";

  if (typeof selectorOrElements === "string") { // Selector string
    document.querySelectorAll(selectorOrElements).forEach(element => {
      element.classList.add('d-none');
      element.setAttribute('hidden', 'true');
    });
  } else if (typeof selectorOrElements === "object") {
    if (Object.prototype.isPrototypeOf.call(NodeList.prototype, selectorOrElements)) { // NodeList from querySelectorAll
      selectorOrElements.forEach(element => {
        element.classList.add('d-none');
        element.setAttribute('hidden', 'true');
      });
    } else if (Object.prototype.isPrototypeOf.call(Node.prototype, selectorOrElements)) { // Single node from querySelector
      selectorOrElements.classList.add('d-none');
      selectorOrElements.setAttribute('hidden', 'true');
    } else {
      throw new Error(errorMessage);
    }
  } else {
    throw new Error(errorMessage);
  }
}

export function showElement(selectorOrElements) {
  let errorMessage = "Unrecognized type for first argument 'selectorOrElements'. The value must be a selector string, Node, or NodeList.";

  if (typeof selectorOrElements === "string") { // Selector string
    document.querySelectorAll(selectorOrElements).forEach(element => {
      element.classList.remove('d-none');
      element.removeAttribute('hidden');
    });
  } else if (typeof selectorOrElements === "object") {
    if (Object.prototype.isPrototypeOf.call(NodeList.prototype, selectorOrElements)) { // NodeList from querySelectorAll
      selectorOrElements.forEach(element => {
        element.classList.remove('d-none');
        element.removeAttribute('hidden');
      });
    } else if (Object.prototype.isPrototypeOf.call(Node.prototype, selectorOrElements)) { // Single Node from querySelector
      selectorOrElements.classList.remove('d-none');
      selectorOrElements.removeAttribute('hidden');
    } else {
      throw new Error(errorMessage);
    }
  } else {
    throw new Error(errorMessage);
  }
}

export function insertAfter(newNode, referenceNode) {
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
}

/**
 * emulates jQuery's ability to create a new DOM element from an HTML string
 * @see https://stackoverflow.com/a/494348
 */
export function createElementFromHTML(htmlString, parentElement = 'div', single = false) {
  /*
  By default, this function uses a <div> as a temporary container element, but you can pass in another tag as the second
  argument. A <div> works for many scenarios, but not everything. Notable exceptions include a <td> which must be inside
  a <tr>, or <li> which must be inside an <ol> or <ul>. If the top-level elements in your string contain one of these
  special cases, you must pass in the proper parent element. If you don't, the HTML will be invalid and this function
  will fail.
  */
  let container = document.createElement(parentElement);

  container.innerHTML = htmlString.trim();

  /*
  If you know this function will only create one top-level element, you can pass in true as the last argument to return
  a single element instead of a list of elements. This allows you to use it directly without a forEach loop.
  */
  if (single) {
    return container.firstElementChild;
  } else {
    return container.childNodes;
  }
}

/**
 * Dynamically update an alert component
 *
 * @param {string|Node} alertElement The alert element to update. Can be a selector string or a single element.
 *                        'alert-' prefix. Optional, defaults to 'info'.
 * @param {string} text The text or HTML content to inject into the body of the alert
 *                       Or pass in an empty string to hide the icon.
 * @param {?string} style The Bootstrap alert style class that controls the overall color scheme. Don't include the
 *                        'alert-' prefix. Optional, defaults to 'info'.
 * @param {?string} icon The name of a Material icon. Omit this parameter to use the default icon of the provided style.
 *                       Or pass in an empty string to hide the icon.
 */
export function updateAlert(alertElement, text, style = 'info', icon = null) {
  if (!alertElement) {
    throw new Error("The 'alertElement' parameter is required.");
  }

  if (!text) {
    throw new Error("The 'text' parameter is required.");
  }

  style = style ?? 'info';

  if (typeof alertElement === 'string') {
    alertElement = query(alertElement);
  }
  let innerAlert = alertElement.querySelector('.alert');
  let iconElement = innerAlert.querySelector('.material-icons');

  let styleClasses = [
    'success',
    'warning',
    'danger',
    'info',
    'light',
    'dark',
  ];

  // Remove any existing style classes
  innerAlert.classList.forEach(c => {
    if (styleClasses.includes(c.replace('alert-', ''))) {
      innerAlert.classList.remove(c);
    }
  });

  iconElement.classList.forEach(c => {
    if (styleClasses.includes(c.replace('text-', ''))) {
      innerAlert.classList.remove(c);
    }
  });

  innerAlert.classList.add('alert-' + style);
  innerAlert.style = `--bs-alert-border-color: var(--bs-${style});`;
  iconElement.classList.add('text-' + style);

  let textElement = innerAlert.querySelector('.alert-text');
  textElement.innerHTML = text;

  if (icon === '') {
    hideElement(iconElement);
  } else {
    if (!icon) {
      switch (style) {
        case 'success':
          icon = 'check_circle';
          break;
        case 'warning':
          icon = 'warning';
          break;
        case 'danger':
          icon = 'error';
          break;
        case 'info':
          icon = 'info_outline';
          break;
      }
    }

    iconElement.textContent = icon;
    showElement(iconElement);
  }

}

export function firstCharacterToUpperCase(stringValue) {
  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
}

export { query, queryAll };
